import React, { useEffect, useState, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import Heading from '@tds/core-heading';
import Modal from '@tds/community-modal';
import InputGroup from '@tds/community-input-group';

import MessageNotification from '../messageNotification';
import ModalCustom from '../design/shared/modal';
import Table from '../design/shared/table';
import { LinkButton, ContentWrapper, Box } from '../design/css';
import UserForm from './form';
import request from '../utils/request';
import { setConstant } from '../utils/utils';
import ValidateLoader from '../UI/validateLoader';
import { CONSTANT } from '../utils/constant';
import { Search } from './css';
import { TranslationsContext } from '../../context/translations';

const getUsersUrl = (page, q, baseUrl = '/users') => {
  let url = baseUrl;
  let op = '?';

  if (page) {
    url = url.concat(`?page=${page}`);
    op = '&';
  }
  if (q) url = url.concat(`${op}q=${q}`);

  return url;
};

const fetchUsers = url => {
  return request.get(url);
};

const fetchUser = id => {
  return request.get(`/users/${id}`);
};

const fetchRoles = () => {
  return request.get('/roles');
};

const fetchChannels = () => {
  return request.get('/channels');
};

const fetchProvinces = () => {
  return request.get('/provinces');
};

const fetchDealerships = () => {
  return request.get('/dealerships');
};

const modalCustomStyles = `
  max-height: 90%;
  top: 5%;
`;

const Users = ({ history, location: { search }, match: { params } }) => {
  const { page, q } = queryString.parse(search);
  let { userId } = params;
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState();
  const focusElementOnClose = useRef(null);
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState();
  const [provinces, setProvinces] = useState();
  const [channels, setChannels] = useState();
  const [dealerships, setDealerships] = useState();
  const [action, setAction] = useState('');
  const [dialogUser, setDialogUser] = useState();
  const [showTelusModal, setShowTelusModal] = useState(false);
  const [searchString, setSearchString] = useState(q);
  const [messageNotification, setMessageNotification] = useState();
  const translations = useContext(TranslationsContext);
  const {
    general,
    USERS: { LIST: usersLabels }
  } = translations;

  const tableHeaders = {
    first_name: usersLabels.COLUMNS.FIRST_NAME,
    last_name: usersLabels.COLUMNS.LAST_NAME,
    email: usersLabels.COLUMNS.EMAIL,
    role_description: usersLabels.COLUMNS.ROLE,
    actions: usersLabels.COLUMNS.ACTIONS
  };

  const ACTIONS = {
    DELETE: usersLabels.ACTIONS.DELETE,
    EDIT: usersLabels.ACTIONS.EDIT,
    APPROVE: usersLabels.ACTIONS.APPROVE,
    DECLINE: usersLabels.ACTIONS.DECLINE
  };

  const handleUsers = async ({ data }) => {
    if (!data) return;
    const tempUsers = data.users.map(u => {
      return { ...u, role_description: u.role.description };
    });
    setUsers({ data: tempUsers, count: data.count });
    if (userId) {
      const userIfo = await fetchUser(userId);
      document.body.style.overflow = 'hidden';
      setUser(userIfo && userIfo.data);
    }
  };

  const getUsers = async () => {
    setIsLoading(true);
    const response = await fetchUsers(getUsersUrl(page, q));
    await handleUsers(response);
    setIsLoading(false);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    Promise.allSettled([
      fetchUsers(getUsersUrl(page, q)),
      fetchRoles(),
      fetchChannels(),
      fetchProvinces(),
      fetchDealerships()
    ]).then(
      async ([
        usersResp,
        rolesResp,
        channelsResp,
        provincesResp,
        dealershipsResp
      ]) => {
        await handleUsers(usersResp);

        const {
          value: {
            data: { roles: apiRoles }
          }
        } = rolesResp;
        setRoles(apiRoles);
        setConstant('ROLES', apiRoles, 'name');

        const {
          value: {
            data: { channels: apiChannels }
          }
        } = channelsResp;
        setChannels(apiChannels);
        setConstant('CHANNELS', apiChannels, 'name');

        const {
          value: {
            data: { provinces: apiProvinces }
          }
        } = provincesResp;
        setProvinces(apiProvinces);

        const {
          value: {
            data: { dealerships: apiDealerships }
          }
        } = dealershipsResp;
        setDealerships(apiDealerships);

        setIsLoading(false);
      }
    );
  }, []);

  useEffect(() => {
    if (!user) {
      setMessageNotification();
      getUsers();
    }
  }, [page, q]);

  const editUser = row => {
    document.body.style.overflow = 'hidden';
    setMessageNotification();
    setUser(row);
    history.push(getUsersUrl(page, q, `/users/${row.id}`));
  };

  const closeModal = url => {
    document.body.style.overflow = 'unset';
    setUser();
    history.push(url);
  };

  const openTelusModal = () => {
    setShowTelusModal(true);
  };

  const closeTelusModal = () => {
    setShowTelusModal(false);
  };

  const successActionHandling = msg => {
    setMessageNotification(msg);
    getUsers();
    setDialogUser();
    setAction('');
    closeTelusModal();
  };

  const successfulUpdate = (msg, url) => {
    setMessageNotification(msg);
    userId = undefined;
    closeModal(url);
    getUsers();
  };

  const approveUser = row => {
    setDialogUser(row);
    setAction(ACTIONS.APPROVE);
    openTelusModal();
  };

  const declineUser = row => {
    setDialogUser(row);
    setAction(ACTIONS.DECLINE);
    openTelusModal();
  };

  const deleteUser = row => {
    setDialogUser(row);
    setAction(ACTIONS.DELETE);
    openTelusModal();
  };

  const proceed = () => {
    switch (action) {
      case ACTIONS.DELETE:
        request
          .post(`/users/${dialogUser.id}/delete`, dialogUser)
          .then(({ data }) => successActionHandling(data.status));
        break;
      case ACTIONS.DECLINE:
        request
          .post(`/users/${dialogUser.id}/delete`, dialogUser)
          .then(({ data }) => successActionHandling(data.status));
        break;
      case ACTIONS.APPROVE:
        request
          .post(`/users/${dialogUser.id}/approve`, dialogUser)
          .then(({ data }) => successActionHandling(data.status));
        break;
      default:
    }
  };

  const actions = row => {
    return (
      <>
        <LinkButton onClick={() => editUser(row)}>{ACTIONS.EDIT}</LinkButton>|
        <LinkButton onClick={() => deleteUser(row)}>
          {ACTIONS.DELETE}
        </LinkButton>
        {row.status === CONSTANT.KEYS.USER_STATUS.PENDING_APPROVAL && (
          <>
            |
            <LinkButton onClick={() => approveUser(row)}>
              {ACTIONS.APPROVE}
            </LinkButton>
            |
            <LinkButton onClick={() => declineUser(row)}>
              {ACTIONS.DECLINE}
            </LinkButton>
          </>
        )}
      </>
    );
  };

  return (
    <ContentWrapper>
      {isLoading ? (
        <ValidateLoader message={general.loading} />
      ) : (
        <>
          {dialogUser && (
            <Modal
              heading={usersLabels.confirmationDialog.HEADING}
              bodyText={`${usersLabels.confirmationDialog.PRE_TEXT} ${
                usersLabels.ACTIONS[action.toUpperCase()]
              } ${dialogUser.first_name} ${dialogUser.last_name} ${
                dialogUser.email
              }?`}
              confirmCTAText={usersLabels.confirmationDialog.CONFIRM}
              cancelCTAText={usersLabels.confirmationDialog.CANCEL}
              onConfirm={proceed}
              focusElementAfterClose={focusElementOnClose}
              onClose={closeTelusModal}
              isOpen={showTelusModal}
            />
          )}
          <Search>
            <InputGroup
              label={general.search}
              hint={general.searchBy}
              defaultValue={searchString}
              onClearButtonClick={() => setSearchString('')}
              onChange={e => setSearchString(e.target.value)}
              onButtonClick={() =>
                history.push(getUsersUrl(null, searchString))
              }
              onKeyDown={e =>
                e.key === 'Enter' &&
                history.push(getUsersUrl(null, searchString))
              }
            />
          </Search>
          {messageNotification && (
            <Box pt={20} pb={20}>
              <MessageNotification
                variant="success"
                message={messageNotification}
              />
            </Box>
          )}
          {users && users.data && users.data.length ? (
            <Table
              title={usersLabels.TITLE}
              headers={tableHeaders}
              data={users.data}
              actions={actions}
              currentPage={parseInt(page || 1, 10)}
              totalRecords={users.count}
              url={searchString && `?q=${searchString}`}
            />
          ) : (
            <Heading level="h3">{usersLabels.NO_RECORDS}</Heading>
          )}
          {user && roles && channels && provinces && dealerships && (
            <ModalCustom
              open={!!user}
              setOpen={() => closeModal(getUsersUrl(page, q))}
              header={general.userProfile}
              customStyles={modalCustomStyles}
            >
              <UserForm
                user={user}
                roles={roles}
                channels={channels}
                dealerships={dealerships}
                provinces={provinces}
                submissionHandling={successfulUpdate}
                backUrl={getUsersUrl(page, q)}
              />
            </ModalCustom>
          )}
        </>
      )}
    </ContentWrapper>
  );
};

Users.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
    goBack: PropTypes.func
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.object
  }).isRequired
};

export default Users;
